
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  NetworkType, 
  NetworkProvider, 
  MeshNetworkDefinition, 
  RoutingProtocol, 
  InterconnectionMethod,
  ContainerType,
  VMType,
  OSType,
  ResourceAllocation
} from '@/components/dashboard/network/NetworkTypes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Link, Server, Database, Terminal, Cpu, HardDrive, Network, Box, Settings, ArrowLeft } from 'lucide-react';
import PageTransition from '@/components/layout/PageTransition';

// Sample network data for selection
const mockNetworks = [
  { id: 'net-1', name: '5G Tower Network', type: '5g' as NetworkType },
  { id: 'net-2', name: 'Fiber Backbone', type: 'fiber' as NetworkType },
  { id: 'net-3', name: 'Satellite Array', type: 'satcom' as NetworkType },
  { id: 'net-4', name: 'Radio Network', type: 'radio' as NetworkType },
  { id: 'net-5', name: 'Broadband Cluster', type: 'broadband' as NetworkType },
];

// VM and Container configuration types
interface VMConfig {
  enabled: boolean;
  type: VMType;
  os: OSType;
  cpu: number;
  memory: number;
  storage: number;
  isNetworkHub: boolean;
}

interface ContainerConfig {
  enabled: boolean;
  type: ContainerType;
  image: string;
  cpu: number;
  memory: number;
  isNetworkHub: boolean;
  ports: string;
  env: string;
}

const AddMeshConnection: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState<Partial<MeshNetworkDefinition>>({
    name: '',
    networks: [],
    routingProtocol: 'ospf',
    failoverPriority: [],
    interconnectionMethod: 'auto',
    isActive: true,
    createdAt: new Date().toISOString(),
  });

  const [vmConfig, setVmConfig] = useState<VMConfig>({
    enabled: false,
    type: 'kvm',
    os: 'ubuntu',
    cpu: 2,
    memory: 4,
    storage: 50,
    isNetworkHub: false
  });

  const [containerConfig, setContainerConfig] = useState<ContainerConfig>({
    enabled: false,
    type: 'docker',
    image: 'ubuntu:latest',
    cpu: 1,
    memory: 2,
    isNetworkHub: false,
    ports: '80:80,443:443',
    env: 'NODE_ENV=production'
  });

  const [selectedNetworks, setSelectedNetworks] = useState<string[]>([]);
  const [availableNetworks, setAvailableNetworks] = useState(mockNetworks);

  const handleAddNetwork = (networkId: string) => {
    if (!networkId || selectedNetworks.includes(networkId)) return;
    
    setSelectedNetworks([...selectedNetworks, networkId]);
    setFormData({
      ...formData,
      networks: [...(formData.networks || []), networkId]
    });
    
    // Remove from available networks
    setAvailableNetworks(availableNetworks.filter(n => n.id !== networkId));
  };

  const handleRemoveNetwork = (networkId: string) => {
    setSelectedNetworks(selectedNetworks.filter(id => id !== networkId));
    setFormData({
      ...formData,
      networks: (formData.networks || []).filter(id => id !== networkId)
    });
    
    // Add back to available networks
    const networkToAdd = mockNetworks.find(n => n.id === networkId);
    if (networkToAdd) {
      setAvailableNetworks([...availableNetworks, networkToAdd]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleVMChange = (name: string, value: any) => {
    setVmConfig({
      ...vmConfig,
      [name]: value
    });
  };

  const handleContainerChange = (name: string, value: any) => {
    setContainerConfig({
      ...containerConfig,
      [name]: value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.networks?.length) {
      toast.error("Please provide a name and select at least one network");
      return;
    }
    
    // In a real app, we would save to backend/database here
    const newMeshNetwork: MeshNetworkDefinition = {
      id: `mesh-${Date.now()}`,
      name: formData.name || 'New Mesh Network',
      networks: formData.networks || [],
      routingProtocol: formData.routingProtocol as RoutingProtocol || 'ospf',
      failoverPriority: formData.failoverPriority || [],
      interconnectionMethod: formData.interconnectionMethod as InterconnectionMethod || 'auto',
      createdAt: formData.createdAt || new Date().toISOString(),
      isActive: true
    };
    
    // Log the data (in reality we would save to state/backend)
    console.log('Created mesh network:', newMeshNetwork);
    console.log('VM Configuration:', vmConfig.enabled ? vmConfig : 'disabled');
    console.log('Container Configuration:', containerConfig.enabled ? containerConfig : 'disabled');
    
    // Show success message
    toast.success("Mesh connection created successfully!");
    
    // Navigate back to network config page
    navigate('/network-config');
  };

  return (
    <PageTransition>
      <div className="container py-8">
        <div className="flex items-center mb-6 space-x-2">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/networks')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Add Mesh Connection</h1>
        </div>
        
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle>New Mesh Connection</CardTitle>
            <CardDescription>
              Connect multiple networks into a mesh network with VM or Container-based network hubs
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="px-6">
                <TabsList className="grid grid-cols-4 w-full">
                  <TabsTrigger value="basic">Basic Config</TabsTrigger>
                  <TabsTrigger value="networks">Networks</TabsTrigger>
                  <TabsTrigger value="vm">VM Config</TabsTrigger>
                  <TabsTrigger value="container">Container Config</TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="basic" className="p-6">
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Mesh Network Name</Label>
                    <Input 
                      id="name" 
                      name="name"
                      placeholder="Enter network name" 
                      value={formData.name || ''}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Routing Protocol</Label>
                    <Select 
                      value={formData.routingProtocol || 'ospf'} 
                      onValueChange={(value) => handleSelectChange('routingProtocol', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select routing protocol" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ospf">OSPF</SelectItem>
                        <SelectItem value="batman">B.A.T.M.A.N.</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Interconnection Method</Label>
                    <Select 
                      value={formData.interconnectionMethod || 'auto'} 
                      onValueChange={(value) => handleSelectChange('interconnectionMethod', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automatic</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="scalar">Scalar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="networks" className="p-6">
                <div className="space-y-4">
                  <Label>Selected Networks</Label>
                  {selectedNetworks.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No networks selected</p>
                  ) : (
                    <div className="grid gap-2">
                      {selectedNetworks.map(networkId => {
                        const network = mockNetworks.find(n => n.id === networkId);
                        return (
                          <div key={networkId} className="flex items-center justify-between p-2 border rounded-md">
                            <span>{network?.name} ({network?.type})</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleRemoveNetwork(networkId)}
                            >
                              Remove
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label htmlFor="add-network">Add Network</Label>
                      <Select onValueChange={(value) => handleAddNetwork(value)}>
                        <SelectTrigger id="add-network">
                          <SelectValue placeholder="Select network to add" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableNetworks.map(network => (
                            <SelectItem key={network.id} value={network.id}>
                              {network.name} ({network.type})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="vm" className="space-y-6 p-6">
                <div className="flex items-center justify-between pb-4 border-b">
                  <div className="flex items-center space-x-2">
                    <Server className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-medium">Virtual Machine Configuration</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch 
                      id="vm-enabled"
                      checked={vmConfig.enabled}
                      onCheckedChange={(checked) => handleVMChange('enabled', checked)}
                    />
                    <Label htmlFor="vm-enabled">Enable VM</Label>
                  </div>
                </div>
                
                {vmConfig.enabled && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>VM Type</Label>
                        <Select 
                          value={vmConfig.type} 
                          onValueChange={(value) => handleVMChange('type', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kvm">KVM</SelectItem>
                            <SelectItem value="hyperv">Hyper-V</SelectItem>
                            <SelectItem value="vmware">VMware</SelectItem>
                            <SelectItem value="xen">Xen</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Operating System</Label>
                        <Select 
                          value={vmConfig.os} 
                          onValueChange={(value) => handleVMChange('os', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ubuntu">Ubuntu</SelectItem>
                            <SelectItem value="debian">Debian</SelectItem>
                            <SelectItem value="centos">CentOS</SelectItem>
                            <SelectItem value="windows">Windows Server</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label>CPU Cores: {vmConfig.cpu}</Label>
                      </div>
                      <Slider 
                        value={[vmConfig.cpu]} 
                        min={1} 
                        max={16} 
                        step={1} 
                        onValueChange={(value) => handleVMChange('cpu', value[0])}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label>Memory (GB): {vmConfig.memory}</Label>
                      </div>
                      <Slider 
                        value={[vmConfig.memory]} 
                        min={1} 
                        max={64} 
                        step={1} 
                        onValueChange={(value) => handleVMChange('memory', value[0])}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label>Storage (GB): {vmConfig.storage}</Label>
                      </div>
                      <Slider 
                        value={[vmConfig.storage]} 
                        min={10} 
                        max={1000} 
                        step={10} 
                        onValueChange={(value) => handleVMChange('storage', value[0])}
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2 pt-2">
                      <Switch 
                        id="vm-hub"
                        checked={vmConfig.isNetworkHub}
                        onCheckedChange={(checked) => handleVMChange('isNetworkHub', checked)}
                      />
                      <Label htmlFor="vm-hub">Configure as Network Hub</Label>
                    </div>
                    
                    {vmConfig.isNetworkHub && (
                      <div className="p-4 border rounded-md bg-muted/50">
                        <h4 className="font-medium flex items-center gap-2 mb-2">
                          <Network className="h-4 w-4" />
                          Network Hub Configuration
                        </h4>
                        <p className="text-sm text-muted-foreground mb-2">
                          This VM will be configured as a network hub with routing and traffic management capabilities.
                        </p>
                        <div className="space-y-2">
                          <RadioGroup defaultValue="automatic">
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="automatic" id="r1" />
                              <Label htmlFor="r1">Automatic configuration</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="manual" id="r2" />
                              <Label htmlFor="r2">Manual configuration</Label>
                            </div>
                          </RadioGroup>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="container" className="space-y-6 p-6">
                <div className="flex items-center justify-between pb-4 border-b">
                  <div className="flex items-center space-x-2">
                    <Box className="h-5 w-5 text-primary" />
                    <h3 className="text-lg font-medium">Container Configuration</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch 
                      id="container-enabled"
                      checked={containerConfig.enabled}
                      onCheckedChange={(checked) => handleContainerChange('enabled', checked)}
                    />
                    <Label htmlFor="container-enabled">Enable Container</Label>
                  </div>
                </div>
                
                {containerConfig.enabled && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Container Type</Label>
                        <Select 
                          value={containerConfig.type} 
                          onValueChange={(value) => handleContainerChange('type', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="docker">Docker</SelectItem>
                            <SelectItem value="kubernetes">Kubernetes</SelectItem>
                            <SelectItem value="podman">Podman</SelectItem>
                            <SelectItem value="lxc">LXC</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Container Image</Label>
                        <Input 
                          placeholder="e.g., ubuntu:latest" 
                          value={containerConfig.image}
                          onChange={(e) => handleContainerChange('image', e.target.value)}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label>CPU Units: {containerConfig.cpu}</Label>
                      </div>
                      <Slider 
                        value={[containerConfig.cpu]} 
                        min={0.5} 
                        max={8} 
                        step={0.5} 
                        onValueChange={(value) => handleContainerChange('cpu', value[0])}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label>Memory (GB): {containerConfig.memory}</Label>
                      </div>
                      <Slider 
                        value={[containerConfig.memory]} 
                        min={0.5} 
                        max={16} 
                        step={0.5} 
                        onValueChange={(value) => handleContainerChange('memory', value[0])}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Exposed Ports (host:container, comma separated)</Label>
                      <Input 
                        placeholder="e.g., 80:80, 443:443" 
                        value={containerConfig.ports}
                        onChange={(e) => handleContainerChange('ports', e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Environment Variables (key=value, comma separated)</Label>
                      <Input 
                        placeholder="e.g., NODE_ENV=production" 
                        value={containerConfig.env}
                        onChange={(e) => handleContainerChange('env', e.target.value)}
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2 pt-2">
                      <Switch 
                        id="container-hub"
                        checked={containerConfig.isNetworkHub}
                        onCheckedChange={(checked) => handleContainerChange('isNetworkHub', checked)}
                      />
                      <Label htmlFor="container-hub">Configure as Network Hub</Label>
                    </div>
                    
                    {containerConfig.isNetworkHub && (
                      <div className="p-4 border rounded-md bg-muted/50">
                        <h4 className="font-medium flex items-center gap-2 mb-2">
                          <Network className="h-4 w-4" />
                          Network Hub Configuration
                        </h4>
                        <p className="text-sm text-muted-foreground mb-2">
                          This container will be configured as a network hub with routing and traffic management capabilities.
                        </p>
                        <div className="space-y-4">
                          <div className="flex items-center space-x-2">
                            <Switch id="net-host" />
                            <Label htmlFor="net-host">Use host network</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch id="privileged" />
                            <Label htmlFor="privileged">Run in privileged mode</Label>
                          </div>
                          <div className="space-y-2">
                            <Label>Network Mode</Label>
                            <Select defaultValue="bridge">
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="bridge">Bridge</SelectItem>
                                <SelectItem value="host">Host</SelectItem>
                                <SelectItem value="overlay">Overlay</SelectItem>
                                <SelectItem value="macvlan">Macvlan</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
            
            <CardFooter className="flex justify-between px-6 pb-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => navigate('/networks')}
              >
                Cancel
              </Button>
              <Button type="submit">Create Mesh Connection</Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </PageTransition>
  );
};

export default AddMeshConnection;
